import React from 'react';
import { IonBadge, IonButton, IonCheckbox, IonIcon, IonPopover, IonText } from '@ionic/react';
import { banOutline, chevronDownOutline, chevronForwardOutline, ellipsisHorizontalOutline } from 'ionicons/icons';
import {
  ActionAccordionActionContext,
  ActionAccordionAdditionalColumn,
  ActionAccordionIconActionColumn,
  ActionAccordionRowData,
  ActionAccordionTextColumn,
} from '@/types/model/actionAccordion';
import { getContentCount } from '@/services/util/AccordionUtils';
import ActionIconButton from '@/components/common/ActionIconButton';
import './AccordionRow.css';

interface AccordionRowProps {
  row: ActionAccordionRowData;
  depth: number;
  pathIds: number[];
  selectedRows: ActionAccordionRowData[];
  isSmallScreen: boolean;
  onToggleExpanded: (row: ActionAccordionRowData) => void;
  onToggleSelected: (row: ActionAccordionRowData, checked: boolean) => void;
  onRunRowAction: (row: ActionAccordionRowData, pathIds: number[], actionColumn: ActionAccordionAdditionalColumn) => void;
  isRowActionRunning: (rowId: number, actionColumnId: string) => boolean;
}

const baseRowGridTemplate = 'minmax(0, 1fr)';

const AccordionRow: React.FC<AccordionRowProps> = ({
  row,
  depth,
  pathIds,
  selectedRows,
  isSmallScreen,
  onToggleExpanded,
  onToggleSelected,
  onRunRowAction,
  isRowActionRunning,
}) => {
  const [actionsPopoverEvent, setActionsPopoverEvent] = React.useState<Event>();
  const hasContent = row.content !== undefined && row.content !== null;
  const hasChildren = (row.children?.length ?? 0) > 0;
  const canExpand = row.isExpandable ?? (hasContent || hasChildren);
  const hasCountBadge = hasChildren || row.contentCount !== undefined;

  const actionContext: ActionAccordionActionContext = {
    row,
    rowId: row.id,
    rowType: row.type,
    pathIds,
    selectedRows,
  };

  const textColumns = (row.additionalColumns ?? []).filter(
    (column): column is ActionAccordionTextColumn => column.kind === 'text'
  );
  const visibleTextColumns = textColumns.filter(
    (column) => !(isSmallScreen && column.hideOnSmallScreen)
  );

  const actionColumns = (row.additionalColumns ?? []).filter(
    (column): column is ActionAccordionIconActionColumn => column.kind === 'action'
  );
  const visibleActionColumns = actionColumns.filter(
    (column) => !column.isVisible || column.isVisible(actionContext)
  );

  const rowGridTemplate = [
    '44px',
    '28px',
    '36px',
    baseRowGridTemplate,
    ...visibleTextColumns.map(() => isSmallScreen ? 'minmax(0, 0.7fr)' : 'minmax(90px, 0.7fr)'),
    ...(visibleActionColumns.length > 0 ? ['84px'] : []),
  ]
    .filter((part): part is string => Boolean(part))
    .join(' ');

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          marginLeft: `${depth * 16}px`,
          marginRight: `${depth * 16}px`,
          background: 'var(--ion-color-step-150, #eceff4)',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(18, 24, 40, 0.12)',
        }}
      >
        <div
          className="accordionRow"
          style={{
            display: 'grid',
            gridTemplateColumns: rowGridTemplate,
            alignItems: 'center',
            gap: '5px',
            borderRadius: '0',
            height: '52px',
          }}
        >
          {canExpand ? (
            <div
              role="button"
              tabIndex={0}
              aria-label={row.isExpanded ? 'Collapse row' : 'Expand row'}
              style={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                onToggleExpanded(row);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  onToggleExpanded(row);
                }
              }}
            >
              <IonIcon
                icon={row.isExpanded ? chevronDownOutline : chevronForwardOutline}
                style={{ fontSize: '1rem', color: 'var(--ion-color-medium, #6b7280)' }}
              />
            </div>
          ) : (
            <div aria-hidden="true" />
          )}

          {hasCountBadge ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <IonBadge
                style={{
                  minWidth: '14px',
                  height: '14px',
                  borderRadius: '999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--ion-color-step-250, #d4d8de)',
                  color: 'var(--ion-text-color, #2d3035)',
                  fontSize: `${(1 - depth * 0.2) / 2}rem`,
                  fontWeight: 500,
                }}
              >
                {getContentCount(row)}
              </IonBadge>
            </div>
          ) : (
            <div aria-hidden="true" />
          )}

          {row.isSelectable ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <IonCheckbox
                checked={Boolean(row.isSelected)}
                onClick={(event) => event.stopPropagation()}
                onIonChange={(event) => {
                  event.stopPropagation();
                  onToggleSelected(row, Boolean(event.detail.checked));
                }}
              />
            </div>
          ) : (
            <div aria-hidden="true" />
          )}

          <div
            role="button"
            tabIndex={canExpand ? 0 : -1}
            style={{ cursor: canExpand ? 'pointer' : 'default', minWidth: 0 }}
            onClick={() => {
              if (canExpand) {
                onToggleExpanded(row);
              }
            }}
            onKeyDown={(event) => {
              if (!canExpand) {
                return;
              }
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onToggleExpanded(row);
              }
            }}
          >
            <IonText
              title={row.title}
              style={{
                display: 'block',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: `${1 - depth * 0.15}rem`,
                fontWeight: `${600 - depth * 200}`,
                color: 'var(--ion-text-color, #1c2433)',
              }}
            >
              <b>{row.number}.</b> {row.title}
              {row.isActive === false && (
                <IonIcon
                  icon={banOutline}
                  title="Inactive"
                  aria-label="Inactive"
                  style={{ marginLeft: '6px', fontSize: '0.9em', color: 'var(--ion-color-warning)' }}
                />
              )}
            </IonText>
          </div>

          {visibleTextColumns.map((column) => (
              <IonText
                key={column.id}
                title={column.text}
                style={{
                  fontSize: '0.8rem',
                  color: column.color ?? 'var(--ion-color-medium-shade, #4b5563)',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {column.text}
              </IonText>
          ))}

          {visibleActionColumns.length === 1 && (() => {
            const [column] = visibleActionColumns;
            const running = isRowActionRunning(row.id, column.id);
            const disabled = running || Boolean(column.isDisabled?.(actionContext));

            return (
              <div key={column.id} style={{ display: 'flex', justifyContent: 'center' }}>
                <ActionIconButton
                  icon={column.icon}
                  label={column.title}
                  disabled={disabled}
                  loading={running}
                  onClick={() => onRunRowAction(row, pathIds, column)}
                  buttonStyle={{ margin: 0, color: column.color ?? 'var(--ion-color-primary, #4b5563)' }}
                />
              </div>
            );
          })()}

          {visibleActionColumns.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <IonButton
                fill="clear"
                size="small"
                title="More actions"
                aria-label="More actions"
                onClick={(event) => {
                  event.stopPropagation();
                  setActionsPopoverEvent(event.nativeEvent);
                }}
                style={{ width: '36px', minWidth: '36px', height: '36px', margin: 0 }}
              >
                <IonIcon icon={ellipsisHorizontalOutline} slot="icon-only" />
              </IonButton>
              <IonPopover
                isOpen={Boolean(actionsPopoverEvent)}
                event={actionsPopoverEvent}
                onDidDismiss={() => setActionsPopoverEvent(undefined)}
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(72px, 1fr))', gap: '4px', padding: '8px' }}>
                  {visibleActionColumns.map((column) => {
                    const running = isRowActionRunning(row.id, column.id);
                    const disabled = running || Boolean(column.isDisabled?.(actionContext));

                    return (
                      <ActionIconButton
                        key={column.id}
                        icon={column.icon}
                        label={column.title}
                        disabled={disabled}
                        loading={running}
                        onClick={() => {
                          setActionsPopoverEvent(undefined);
                          onRunRowAction(row, pathIds, column);
                        }}
                        buttonStyle={{ width: '100%', margin: 0, color: column.color ?? 'var(--ion-color-primary, #4b5563)' }}
                      />
                    );
                  })}
                </div>
              </IonPopover>
            </div>
          )}

        </div>
      </div>

      {row.isExpanded && hasContent && (
        <div
          style={{
            marginLeft: `${depth * 16 + 18}px`,
            marginRight: `${depth * 16 + 18}px`,
            marginTop: '-4px',
            padding: '7px 8px',
            borderLeft: '3px solid var(--ion-color-step-250, #d4d8de)',
            background: 'var(--ion-color-step-100, var(--ion-color-light-tint))',
            borderBottomLeftRadius: '10px',
            borderBottomRightRadius: '10px',
          }}
        >
          {row.content}
        </div>
      )}

      {row.isExpanded && !hasContent && hasChildren && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
          {row.children?.map((child) => (
            <AccordionRow
              key={`${pathIds.join('-')}-${child.id}`}
              row={child}
              depth={depth + 1}
              pathIds={[...pathIds, child.id]}
              selectedRows={selectedRows}
              isSmallScreen={isSmallScreen}
              onToggleExpanded={onToggleExpanded}
              onToggleSelected={onToggleSelected}
              onRunRowAction={onRunRowAction}
              isRowActionRunning={isRowActionRunning}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AccordionRow;
